/**
 * Thesys Demo Page
 *
 * Steel thread demonstration of Thesys C1 integration in Caravel.
 * Shows AI-generated interactive visualizations for asset allocation analysis.
 *
 * Located under Planning Agent section in workspace navigation.
 */

import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { CustomC1ChatWidget } from './CustomC1ChatWidget';
import { apiUrl } from '@app/const';
import { getAccessToken } from '@app/api/getAccessToken';
import { store } from '@app/redux/store';

export const ThesysDemoPage: React.FC = () => {
  const params = useParams();
  const planResultRootId = params.planResultRootId;
  const planResultTypeId = params.planResultTypeId;

  // Construct API URL following URL_PATH_SETUP_CHANGES.md pattern
  const fullApiUrl = useMemo(() => {
    const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    return `${baseUrl}/api/thesys-demo`;
  }, []);

  // Intercept fetch requests to add Authorization header
  // C1Chat makes fetch calls internally - we intercept to add auth
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
          ? input.toString()
          : input.url;

      // Only intercept requests to /api/thesys-demo
      if (url.includes('/api/thesys-demo')) {
        try {
          // Get access token from store
          const accessToken = await getAccessToken(store);

          // Add Authorization header if not already present
          const headers = new Headers(init?.headers);
          if (accessToken && !headers.has('Authorization')) {
            headers.set('Authorization', `Bearer ${accessToken}`);
          }

          // Ensure Content-Type is set for JSON
          if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
          }

          // Make request with modified headers
          return originalFetch(input, {
            ...init,
            headers,
          });
        } catch (error) {
          console.error('Failed to get access token:', error);
          return originalFetch(input, init);
        }
      }

      return originalFetch(input, init);
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  // Check for required parameters
  if (!planResultRootId || !planResultTypeId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Missing Required Parameters</Typography>
        <Typography variant="body1" color="text.secondary">
          planResultRootId and planResultTypeId are required
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Optional header - can be removed for full-screen experience */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="h5">Thesys C1 Demo</Typography>
        <Typography variant="body2" color="text.secondary">
          AI-Powered Asset Allocation Analysis with Interactive Components
        </Typography>
      </Box>

      {/* Custom C1 Chat Widget - renders interactive C1 components */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <CustomC1ChatWidget apiUrl={fullApiUrl} />
      </Box>
    </Box>
  );
};
